import React from 'react';
import { AlertTriangle, Clock, FileText, MessageSquare } from 'lucide-react';
import type { HandoffMessage } from './MinhaMesaHandoff';
import type { ProcessArtifact } from '../services/artifactService';

// ============================================================================
// MOTOR DE REGRAS — gera instruções contextuais baseadas no estado operacional
// ============================================================================

export type InstructionSeverity = 'critical' | 'warning' | 'info';

export interface SmartInstruction {
  id: string;
  severity: InstructionSeverity;
  message: string;
}

interface SmartInstructionsInput {
  slaStatus: 'ok' | 'warning' | 'violated' | 'none';
  slaLabel: string;
  handoff: HandoffMessage | null;
  artifacts: ProcessArtifact[];
  hasInstruction: boolean;
  isOverdue: boolean;
}

export function computeSmartInstructions(input: SmartInstructionsInput): SmartInstruction[] {
  const instructions: SmartInstruction[] = [];

  // Rule 1: SLA violated → critical alert
  if (input.slaStatus === 'violated') {
    instructions.push({
      id: 'sla-violated',
      severity: 'critical',
      message: `SLA violado — ${input.slaLabel}. Priorize a conclusão imediata desta etapa.`,
    });
  }

  // Rule 2: SLA warning → warning
  if (input.slaStatus === 'warning') {
    instructions.push({
      id: 'sla-warning',
      severity: 'warning',
      message: `SLA em risco — ${input.slaLabel}. Conclua antes do prazo.`,
    });
  }

  // Rule 3: Handoff message with critical keywords → critical highlight
  if (input.handoff?.message) {
    const msg = input.handoff.message.toLowerCase();
    const criticalKeywords = ['urgente', 'crítico', 'atenção', 'bloqueio', 'problema', 'erro', 'falha', 'retrabalho'];
    const hasCritical = criticalKeywords.some((kw) => msg.includes(kw));
    if (hasCritical) {
      instructions.push({
        id: 'handoff-critical',
        severity: 'critical',
        message: `Mensagem crítica da etapa anterior: "${input.handoff.message.slice(0, 80)}${input.handoff.message.length > 80 ? '...' : ''}"`,
      });
    }
  }

  // Rule 4: Artifact updated recently (last 30min) → info
  const thirtyMinAgo = Date.now() - 30 * 60_000;
  const recentArtifacts = input.artifacts.filter(
    (a) => new Date(a.updatedAt).getTime() > thirtyMinAgo && a.currentVersion > 1,
  );
  if (recentArtifacts.length > 0) {
    const names = recentArtifacts.map((a) => a.name).join(', ');
    instructions.push({
      id: 'artifact-updated',
      severity: 'info',
      message: `Artefato atualizado recentemente: ${names}. Verifique antes de prosseguir.`,
    });
  }

  // Rule 5: No SOP defined → warning
  if (!input.hasInstruction) {
    instructions.push({
      id: 'no-sop',
      severity: 'warning',
      message: 'Esta etapa não possui SOP definido. Solicite orientações ao gestor se necessário.',
    });
  }

  return instructions;
}

// ============================================================================
// COMPONENTE — banner acima do canvas
// ============================================================================

interface SmartInstructionsBannerProps {
  instructions: SmartInstruction[];
}

const severityConfig: Record<InstructionSeverity, {
  bg: string;
  border: string;
  icon: React.ReactNode;
  text: string;
}> = {
  critical: {
    bg: 'bg-red-50 dark:bg-red-950/20',
    border: 'border-red-200 dark:border-red-800/40',
    icon: <AlertTriangle size={12} className="text-red-600 dark:text-red-400 shrink-0" />,
    text: 'text-red-700 dark:text-red-400',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/20',
    border: 'border-amber-200 dark:border-amber-800/40',
    icon: <Clock size={12} className="text-amber-600 dark:text-amber-400 shrink-0" />,
    text: 'text-amber-700 dark:text-amber-400',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/20',
    border: 'border-blue-200 dark:border-blue-800/40',
    icon: <FileText size={12} className="text-blue-600 dark:text-blue-400 shrink-0" />,
    text: 'text-blue-700 dark:text-blue-400',
  },
};

export const SmartInstructionsBanner: React.FC<SmartInstructionsBannerProps> = ({ instructions }) => {
  if (instructions.length === 0) return null;

  return (
    <div className="border-b border-[var(--border-default)] px-5 py-2 space-y-1">
      <div className="flex items-center gap-1.5 mb-1">
        <MessageSquare size={10} className="enterprise-text-muted" />
        <span className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] enterprise-text-muted">
          Instruções operacionais
        </span>
      </div>
      {instructions.map((inst) => {
        const cfg = severityConfig[inst.severity];
        return (
          <div
            key={inst.id}
            className={`flex items-start gap-2 rounded px-2.5 py-1.5 border ${cfg.bg} ${cfg.border}`}
          >
            {cfg.icon}
            <span className={`text-[11px] leading-relaxed ${cfg.text}`}>{inst.message}</span>
          </div>
        );
      })}
    </div>
  );
};
